// ONE PAGE OF A SALES BOOK AT A TIME (design-system §3.17 rev-7, D-560). The
// customer, quote, order, deal, task, review and price-list books read the
// server a cursor page (50 rows) at a time. They used to fold every page a
// reader asked for into one growing table behind "Show more", which past 50
// rows is what rev-7 forbids. Each now shows the page its cursor reads and
// moves by Previous and Next, and a new filter, sort or saved view starts
// again at page 1 (the stack is keyed on the query).
//
// The window is taken from the stack that read the rows on screen
// (`useStackWindow`): while the next page is read the query keeps the page
// shown (`keepPageOnTurn`), so its range stays put until the new rows land, a
// second press while a page is turning does nothing (its cursor would be the
// shown page's again), and a page past the first that empties under the reader
// steps back rather than claiming the book is empty.

import type { TFunction } from "i18next";
import type { DataTableClientPager, DataTablePager } from "../../../../components/data-table";
import {
  useCursorStack,
  useStackWindow,
  type PageWindow,
  type StackRead,
} from "../../../../lib/cursor-pages";

export { keepPageOnTurn } from "../../../../lib/cursor-pages";

/** The cursor stack of one book, back to page 1 whenever `resetKey` changes. */
export function useBookPages(resetKey: string) {
  return useCursorStack(resetKey);
}

/** What a book's read says about the page on screen. */
export type BookRead<T> = StackRead<T>;

/** The window over the rows on screen; see `useStackWindow`. */
export function useBookWindow<T>(
  pages: ReturnType<typeof useBookPages>,
  read: BookRead<T>,
): PageWindow<T> {
  return useStackWindow(pages, read);
}

/** "Rows 51–100" in the reader's digits. */
export function bookRange(
  t: TFunction,
  locale: string,
  view: { readonly from: number; readonly to: number },
): string {
  const n = new Intl.NumberFormat(locale);
  return t("sales.common.pageRange", { from: n.format(view.from), to: n.format(view.to) });
}

/**
 * The DataTable footer for a book, or none while the book fits on one page.
 * `onNext` and `onPrev` are the stack's moves; a press while a page is still
 * turning is dropped.
 */
export function bookPager<T>(
  t: TFunction,
  locale: string,
  view: PageWindow<T>,
  turn: { readonly turning: boolean; readonly onNext: () => void; readonly onPrev: () => void },
): DataTablePager | undefined {
  if (!view.hasPrev && !view.hasNext) return undefined;
  return {
    range: bookRange(t, locale, view),
    meta: turn.turning ? t("sales.common.loading") : undefined,
    page: view.page,
    onPage: (n) => {
      if (turn.turning) return;
      if (n > view.page) turn.onNext();
      else if (n < view.page) turn.onPrev();
    },
    prevLabel: t("sales.common.pagePrev"),
    nextLabel: t("sales.common.pageNext"),
    hasPrev: view.hasPrev,
    hasNext: view.hasNext,
  };
}

/**
 * The words of a client pager on a sales table the screen holds whole (an
 * order's lines, its customer messages): 50 rows a page (D-560). `appendable`
 * marks a table whose rows are added at its end in place (an edit grid, a
 * timeline read further back), which keeps its page when its count moves.
 */
export function bookClientPager(
  t: TFunction,
  locale: string,
  options: { readonly resetKey?: string; readonly appendable?: boolean } = {},
): DataTableClientPager {
  const n = new Intl.NumberFormat(locale);
  return {
    ...(options.resetKey !== undefined ? { resetKey: options.resetKey } : {}),
    ...(options.appendable === true ? { appendable: true } : {}),
    range: (from, to, total) =>
      t("sales.common.pageRangeOf", {
        from: n.format(from),
        to: n.format(to),
        total: n.format(total),
      }),
    prevLabel: t("sales.common.pagePrev"),
    nextLabel: t("sales.common.pageNext"),
  };
}
